// SdsRequestsList.js
// DataTable of sds_requests rows (cards on iPad, table on desktop).

import React from "react";
import { DataTable } from "../ui";
import tableStyles from "../ui/listTable.module.css";
import styles from "./SdsLookupTab.module.css";

// Only 3E-hosted https links are rendered as anchors (mirrors is_allowed_document_url on the server)
const ALLOWED_DOCUMENT_RE = /^https:\/\/(?:[a-z0-9-]+\.)*3eonline\.com(?:[/?#]|$)/i;
export const isAllowedSdsDocumentUrl = (url) =>
  typeof url === "string" && ALLOWED_DOCUMENT_RE.test(url);

const SEARCH_AGAIN_STATUSES = ["Complete", "Revised"];

const STATUS_CLASS = {
  Submitted: styles.statusSubmitted,
  Complete: styles.statusComplete,
  Revised: styles.statusComplete,
  "SDS Not Required": styles.statusDone,
  "Customer Action Required": styles.statusAction,
  Failed: styles.statusFailed,
  Stale: styles.statusFailed,
};

const formatDate = (iso) => (iso ? String(iso).slice(0, 10) : "-");

const StatusCell = ({ row }) => (
  <span className={styles.statusCell}>
    <span className={`${styles.statusPill} ${STATUS_CLASS[row.status] || styles.statusSubmitted}`}>
      {row.status || "Unknown"}
    </span>
    {row.status_detail && <span className={styles.statusDetail}>{row.status_detail}</span>}
  </span>
);

const RequesterCell = ({ row, currentUserId }) => {
  const isMine =
    currentUserId != null &&
    row.requested_by != null &&
    String(row.requested_by) === String(currentUserId);
  return <span>{isMine ? "You" : row.requested_by_name || "-"}</span>;
};

const RowActions = ({ row, onSearchAgain }) => {
  if (isAllowedSdsDocumentUrl(row.document_link)) {
    return (
      <a
        className={styles.viewLink}
        href={row.document_link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
      >
        View SDS
      </a>
    );
  }
  if (SEARCH_AGAIN_STATUSES.includes(row.status)) {
    return (
      <button
        type="button"
        className={styles.searchAgainButton}
        onClick={(event) => {
          event.stopPropagation();
          onSearchAgain?.(row);
        }}
      >
        Search catalog
      </button>
    );
  }
  return null;
};

/**
 * @param {object[]} requests - serialized sds_requests rows
 * @param {boolean} isLoading
 * @param {string|null} error
 * @param {function} onRefresh - reload the list (also the Retry action)
 * @param {string|number} currentUserId - rows by this employee are labelled "You"
 * @param {function} onSearchAgain - (request) hands material_name to the Search sub-tab
 */
export const SdsRequestsList = ({
  requests = [],
  isLoading = false,
  error = null,
  onRefresh,
  currentUserId,
  onSearchAgain,
}) => {
  if (isLoading) {
    return (
      <div className={tableStyles.loadingContainer} data-testid="sds-requests-loading">
        <div className={tableStyles.loadingSpinner} />
        <p>Loading SDS requests...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className={tableStyles.errorContainer} role="alert">
        <p className={tableStyles.errorMessage}>{error}</p>
        <button type="button" className={tableStyles.retryButton} onClick={onRefresh}>
          Retry
        </button>
      </div>
    );
  }

  const columns = [
    { key: "material_name", label: "Product", showInCard: true },
    { key: "manufacturer_name", label: "Manufacturer", showInCard: true },
    {
      key: "status",
      label: "Status",
      showInCard: true,
      render: (value, row) => <StatusCell row={row} />,
    },
    {
      key: "requested_by_name",
      label: "Requested By",
      showInCard: true,
      render: (value, row) => <RequesterCell row={row} currentUserId={currentUserId} />,
    },
    {
      key: "created_at",
      label: "Requested",
      showInCard: true,
      render: (value) => formatDate(value),
    },
    {
      type: "action",
      key: "_actions",
      label: "SDS",
      render: (row) => <RowActions row={row} onSearchAgain={onSearchAgain} />,
    },
  ];

  return (
    <div className={styles.requestsList} data-testid="sds-requests-list">
      <DataTable
        data={requests}
        columns={columns}
        cardActions={(row) => <RowActions row={row} onSearchAgain={onSearchAgain} />}
        emptyMessage="No SDS requests yet."
      />
    </div>
  );
};
